import { useEffect, useRef } from "react";
import type { Control, FieldValues, Path, PathValue, UseFormSetValue } from "react-hook-form";
import { useWatch } from "react-hook-form";

import { IAttribute, IAttributeFieldValue, IAttributeValue } from "@interfaces/attribute.interface";

import { generateDefaultAttributeFieldValues } from "../utils";

/** Extracts only Path keys whose value is IAttributeFieldValue[] */
type AttributeFieldPath<T extends FieldValues> = {
  [K in Path<T>]: PathValue<T, K> extends IAttributeFieldValue[] ? K : never;
}[Path<T>];

interface UseDefaultAttributeValuesOptions<T extends FieldValues> {
  name: AttributeFieldPath<T>;
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  attributes: IAttribute[];
  /** Existing values (editing an entity) used to pre-fill the fields. */
  existingValues?: IAttributeValue[];
}

/**
 * Seeds the form field array with one entry per attribute (pre-filled from existing values).
 * Re-seeds when the attribute set isn't fully covered yet, or when the source entity changes
 * (e.g. picking a different company) so a new entity's values replace the previous one's.
 */
export function useDefaultAttributeValues<T extends FieldValues>({
  name,
  control,
  setValue,
  attributes,
  existingValues,
}: UseDefaultAttributeValuesOptions<T>) {
  const currentValues = useWatch({ control, name: name as Path<T> }) as
    | IAttributeFieldValue[]
    | undefined;
  // Identity of the existingValues we last seeded from. React Query's structural sharing keeps
  // this reference stable across same-data refetches, so it only changes on a real entity switch
  // (or an actual value update) — not on every render, which would clobber in-progress edits.
  const seededFrom = useRef<IAttributeValue[] | undefined>(undefined);

  useEffect(() => {
    if (attributes.length === 0) return;

    const values = currentValues ?? [];
    const covered = attributes.every((attr) => values.some((v) => v.attribute === attr.id));
    const entityChanged = seededFrom.current !== existingValues;

    if (!covered || entityChanged) {
      seededFrom.current = existingValues;
      setValue(
        name as Path<T>,
        generateDefaultAttributeFieldValues(attributes, existingValues) as PathValue<T, Path<T>>
      );
    }
  }, [attributes, existingValues, setValue, currentValues, name]);
}
